import React, { useState, useEffect, useCallback } from "react";
import { Users, User, Briefcase, UsersRound } from "lucide-react";
import { getClientsInfluenceur } from "../../services/InfluenceurService";
import * as LoadingIcons from "react-loading-icons";

// Format date + heure
const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    const options = { day: "2-digit", month: "short", year: "numeric" };
    const datePart = d.toLocaleDateString("fr-FR", options);
    const timePart = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return `${datePart} à ${timePart}`;
};

// Traduire type d'abonnement
const formatAbonnement = (type) => {
    if (!type) return "-";
    return type === "monthly" ? "Mensuel" : type === "yearly" ? "Annuel" : type;
};

// Format nombres
const formatNumber = (num) => {
    if (typeof num === "string") return num;
    if (num < 1000) return num;
    if (num < 1000000) return (num / 1000).toFixed(num % 1000 >= 100 ? 1 : 0) + "K";
    if (num < 1000000000) return (num / 1000000).toFixed(1) + "M";
    return (num / 1000000000).toFixed(1) + "B";
};

const ClientsInfluenceur = () => {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
        total_basic: 0,
        total_pro: 0,
        total_business: 0
    });

    // Récupération des clients depuis le backend
    const fetchClients = useCallback(async (pageNumber = 1) => {
        setLoading(true);
        try {
            const data = await getClientsInfluenceur(pageNumber, pagination.per_page);
            setClients(data.clients);
            setPagination({
                total: data.total,
                per_page: data.per_page,
                current_page: data.current_page,
                last_page: data.last_page,
                total_basic: data.total_basic,
                total_pro: data.total_pro,
                total_business: data.total_business
            });
        } catch (err) {
            console.error("Erreur récupération clients:", err.message);
            setClients([]);
        } finally {
            setLoading(false);
        }
    }, [pagination.per_page]);

    useEffect(() => {
        fetchClients(page);
    }, [page, fetchClients]);

    // KPIs
    const stats = [
        { title: "Clients Basic", value: pagination.total_basic, icon: User, color: "bg-gray-100 text-gray-700" },
        { title: "Clients Pro", value: pagination.total_pro, icon: Users, color: "bg-blue-100 text-blue-700" },
        { title: "Clients Business", value: pagination.total_business, icon: Briefcase, color: "bg-purple-100 text-purple-700" },
        { title: "Total Clients", value: pagination.total, icon: UsersRound, color: "bg-green-100 text-green-700" },
    ];

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page) setPage(newPage);
    };

    return (
        <div className="w-full flex justify-center pb-20 lg:mb-0 mb-16 font-Poppins">
            <div className="w-full max-w-6xl space-y-6">
                <h1 className="text-2xl lg:text-3xl font-extrabold text-start text-color6 mb-6">
                    📊 Clients
                </h1>

                {/* KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
                    {stats.map((stat, idx) => (
                        <div
                            key={idx}
                            className="flex flex-col items-center justify-between bg-white shadow-lg rounded-2xl p-2 lg:p-3"
                        >
                            <div className={`p-2 lg:p-3 rounded-full ${stat.color}`}>
                                <stat.icon size={20} className="xl:size-10 lg:size-10" />
                            </div>
                            <div className="text-center mt-1">
                                <p className="text-sm lg:text-xl text-gray-800 font-semibold lg:font-semibold">{stat.title}</p>
                                <h3 className="text-md lg:text-3xl font-extrabold font-atkinson">
                                    {stat.value > 0 ? formatNumber(stat.value) : "-"}
                                </h3>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tableau clients */}
                <div className="bg-white shadow rounded-xl w-full overflow-x-auto">
                    <table className="w-full min-w-max border-collapse text-center text-xs sm:text-sm lg:text-base">
                        <thead className="bg-color1 text-white sticky top-0">
                            <tr>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:px-4 lg:py-3 lg:font-semibold font-medium">Nom</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:px-4 lg:py-3 lg:font-semibold font-medium">Statut</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:px-4 lg:py-3 lg:font-semibold font-medium">Date inscription</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:px-4 lg:py-3 lg:font-semibold font-medium">Changement statut</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:px-4 lg:py-3 lg:font-semibold font-medium">Prochain paiement</th>
                                <th className="px-2 py-1 sm:px-4 sm:py-2 lg:px-4 lg:py-3 lg:font-semibold font-medium">Périodicité</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="lg:h-[400px] h-[300px] text-center">
                                        <div className="flex justify-center items-center">
                                            <LoadingIcons.SpinningCircles
                                                stroke="#2218eeff"
                                                fill="#2218eeff"
                                                strokeWidth={3}
                                                height={30}
                                                width={30}
                                            />
                                            <span className="ml-2 text-gray-600 font-medium text-sm sm:text-base">Chargement...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : clients.length > 0 ? (
                                clients.map((client) => (
                                    <tr key={client.id} className="border-b hover:bg-gray-50">
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">{client.nom_complet}</td>
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">
                                            <span className={`px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs lg:text-sm rounded-full ${client.current_plan === "basic" ? "bg-gray-700 text-white" :
                                                client.current_plan === "pro" ? "bg-blue-700 text-white" :
                                                    "bg-purple-700 text-white"
                                                }`}>
                                                {client.current_plan}
                                            </span>
                                        </td>
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">{formatDate(client.created_at)}</td>
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">{formatDate(client.plan_debut)}</td>
                                        <td className="px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base">{formatDate(client.prochain_paiement)}</td>
                                        <td className={`px-1 py-1 sm:px-4 sm:py-2 font-medium text-xs sm:text-sm lg:text-base ${client.plan_interval === "monthly" ? "text-green-600" : "text-red-600"}`}>
                                            {formatAbonnement(client.plan_interval)}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="py-2 text-center text-gray-500 text-xs sm:text-sm lg:text-base">Aucun client trouvé.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex w-full justify-center items-center gap-3 mt-4">
                    <button
                        disabled={pagination.current_page === 1}
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        className={`px-4 py-1 rounded-lg text-white text-sm font-semibold transition ${pagination.current_page === 1 ? "bg-gray-400 cursor-not-allowed" : "bg-color1 hover:bg-color6 cursor-pointer"}`}
                    >
                        Précédent
                    </button>
                    <span className="text-color11 font-medium text-sm">
                        {pagination.current_page} / {pagination.last_page}
                    </span>
                    <button
                        disabled={pagination.current_page === pagination.last_page}
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        className={`px-4 py-1 rounded-lg text-white text-sm font-semibold transition ${pagination.current_page === pagination.last_page ? "bg-gray-400 cursor-not-allowed" : "bg-color1 hover:bg-color6 cursor-pointer"}`}
                    >
                        Suivant
                    </button>
                </div>

            </div>
        </div>
    );
};

export default ClientsInfluenceur;
